
import React, { useContext, useMemo, useState, useEffect, useRef } from 'react';
import { Puzzle, Page, PageContextType, UserProfile, GroupGame, defaultImageEdits, getSafeImageEdits } from '../types';
import PostCard from '../components/PostCard';
import { usePuzzles } from '../PuzzleContext';
import BackButton from '../components/BackButton';
import { PageContext } from '../PageContext';
import { isUserOnline } from '../utils';
import { useAuth } from '../AuthContext';
import { useAdmin } from '../AdminContext';
import ReportPostModal from '../components/ReportPostModal';

interface GameDetailPageProps {
    gameId: string;
}

const StatItem: React.FC<{ value: number | string; label: string }> = ({ value, label }) => (
    <div className="text-center">
        <p className="font-bold text-lg">{value}</p>
        <p className="text-xs">{label}</p>
    </div>
);

const JoinRequestCard: React.FC<{
    user: UserProfile;
    onApprove: () => void;
    onDecline: () => void;
}> = ({ user, onApprove, onDecline }) => (
    <div className="flex items-center p-3 space-x-3 bg-white rounded-lg shadow-sm">
        <img src={user.photoURL} alt={user.displayName} className="w-10 h-10 rounded-full object-cover" />
        <p className="flex-1 font-bold truncate">{user.displayName}</p>
        <div className="flex-shrink-0 flex space-x-2">
            <button onClick={onDecline} className="bg-gray-200 text-gray-700 font-bold text-xs px-3 py-1 rounded-md">Decline</button>
            <button onClick={onApprove} className="bg-emerald-500 text-white font-bold text-xs px-3 py-1 rounded-md">Approve</button>
        </div>
    </div>
);

const quotes = [
    "Post a puzzle for the group!",
    "Challenge your friends.",
    "Share a new mystery.",
    "Got a tricky one? Share it here!"
];

const GameDetailPage: React.FC<GameDetailPageProps> = ({ gameId }) => {
    const { userProfile, groups, approveJoinRequest, declineJoinRequest, allUsers, usersLoading } = useAuth();
    const { puzzles, reportPuzzle, puzzlesLoading } = usePuzzles();
    const { reportGroup } = useAdmin();
    const pageContext = useContext(PageContext);
    const [activeTab, setActiveTab] = useState<'feed' | 'leaderboard'>('feed');
    const [isReportModalOpen, setReportModalOpen] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState<string>('All');
    const [quoteIndex, setQuoteIndex] = useState(0);
    const [quoteOpacity, setQuoteOpacity] = useState(1);
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);

    const profilePicRef = useRef<HTMLButtonElement>(null);
    const groupNameRef = useRef<HTMLHeadingElement>(null);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setQuoteOpacity(0);
            setTimeout(() => {
                setQuoteIndex(prevIndex => (prevIndex + 1) % quotes.length);
                setQuoteOpacity(1);
            }, 500);
        }, 20000);
        return () => clearInterval(intervalId);
    }, []);
    
    const game = useMemo(() => groups.find(g => g.id === gameId), [gameId, groups]);

    const gamePuzzles = useMemo(() => {
        if (usersLoading || puzzlesLoading || !userProfile) {
            return [];
        }

        const groupPuzzles = puzzles.filter(p => p.groupId === gameId);

        return groupPuzzles.filter(p => {
            const creator = allUsers.find(u => u.uid === p.creatorId);
            if (!creator) {
                return false;
            }
    
            const iHaveBlockedThem = userProfile.blockedUsers?.includes(p.creatorId);
            const theyHaveBlockedMe = creator.blockedUsers?.includes(userProfile.uid);
            if (iHaveBlockedThem || theyHaveBlockedMe) {
                return false;
            }
            
            return true;
        });
    }, [puzzles, gameId, userProfile, allUsers, usersLoading, puzzlesLoading]);

    const gameMembers = useMemo(() => {
        if (!game || !userProfile) return [];
        return allUsers.filter(u => {
            const iHaveBlockedThem = userProfile.blockedUsers?.includes(u.uid);
            const theyHaveBlockedMe = u.blockedUsers?.includes(userProfile.uid);
            return !iHaveBlockedThem && !theyHaveBlockedMe && game.members.includes(u.uid);
        });
    }, [game, allUsers, userProfile]);
    
    const availableCategories = useMemo(() => {
        if (!gamePuzzles) return ['All'];
        const categories = new Set(gamePuzzles.map(p => p.category));
        return ['All', ...Array.from(categories)];
    }, [gamePuzzles]);

    const filteredGamePuzzles = useMemo(() => {
        if (categoryFilter === 'All') {
            return gamePuzzles;
        }
        return gamePuzzles.filter(p => p.category === categoryFilter);
    }, [gamePuzzles, categoryFilter]);
    
    const feedWithAds = useMemo(() => {
        return filteredGamePuzzles;
    }, [filteredGamePuzzles]);
    
    const sortedMembers = useMemo(() => {
        if (!game) return [];
        return [...gameMembers].sort((a, b) => (game.scores[b.uid] ?? 0) - (game.scores[a.uid] ?? 0));
    }, [gameMembers, game]);
    
    const isLoading = puzzlesLoading || usersLoading || !userProfile || !game;

    const topThree = sortedMembers.slice(0, 3);
    const rest = sortedMembers.slice(3);
    
    const isCurrentUserAdmin = useMemo(() => {
        if (!userProfile || !game) return false;
        return game.admins.includes(userProfile.uid) || userProfile.isAdmin;
    }, [userProfile, game]);

    const requestingUsers = useMemo(() => {
        if (!game || !game.joinRequests) return [];
        return allUsers.filter(u => game.joinRequests.includes(u.uid));
    }, [game, allUsers]);

    const GroupLeaderboardItem: React.FC<{ user: UserProfile, rank: number, isCurrentUser: boolean, animationDelay: string }> = ({ user, rank, isCurrentUser, animationDelay }) => {
        const pageContext = useContext(PageContext);
        const handleProfileClick = () => pageContext?.switchPage(Page.Profile, user.displayName, { userId: user.uid });
        
        return (
            <div onClick={handleProfileClick} style={{ animationDelay }} className={`animate-list-fade-in flex items-center p-3 rounded-lg shadow-sm cursor-pointer ${isCurrentUser ? 'bg-purple-100' : 'bg-white'}`}>
                <span className="font-bold text-lg w-8">{rank}</span>
                <div className="relative flex-shrink-0 mx-4">
                    <img src={user.photoURL} className="w-12 h-12 rounded-full object-cover"/>
                    {isUserOnline(user) && (
                        <span className="absolute bottom-0 right-0 block h-3.5 w-3.5 rounded-full bg-green-500 border-2 border-white" title="Online"></span>
                    )}
                </div>
                <p className="flex-1 font-bold truncate">{user.displayName}</p>
                <p className="font-bold text-lg text-[var(--primary-dark-purple)]">{game?.scores[user.uid] ?? 0} pts</p>
            </div>
        );
    };

    const PodiumCard: React.FC<{ user: UserProfile, rank: number, animationDelay: string }> = ({ user, rank, animationDelay }) => {
        const pageContext = useContext(PageContext);
        const isFirst = rank === 1;
        const borderClass = isFirst ? 'border-yellow-400' : rank === 2 ? 'border-gray-400' : 'border-orange-400';
        const crownColor = isFirst ? 'text-yellow-400' : rank === 2 ? 'text-gray-400' : 'text-orange-400';
        const handleProfileClick = () => pageContext?.switchPage(Page.Profile, user.displayName, { userId: user.uid });

        return (
            <div onClick={handleProfileClick} style={{ animationDelay }} className={`animate-podium-fade-in flex flex-col items-center bg-white p-4 rounded-xl shadow-lg border-b-8 cursor-pointer ${borderClass} ${isFirst ? 'scale-110 z-10' : 'mt-6'}`}>
                <div className="relative">
                    <img src={user.photoURL} alt={user.displayName} className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-md" />
                    {isUserOnline(user) && (
                        <span className="absolute bottom-1 right-1 block h-4 w-4 rounded-full bg-green-500 border-2 border-white" title="Online"></span>
                    )}
                    {isFirst && <i className="ph-duotone ph-crown-simple absolute -top-4 -right-2 text-4xl rotate-12" style={{ color: crownColor }}></i>}
                    <div className={`absolute -bottom-2 w-full flex justify-center`}><div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-white text-lg ${isFirst ? 'bg-yellow-400' : rank === 2 ? 'bg-gray-400' : 'bg-orange-400'}`}>{rank}</div></div>
                </div>
                <p className="font-bold mt-4 truncate">{user.displayName}</p>
                <p className="text-sm text-gray-500 font-bold">{game?.scores[user.uid] ?? 0} pts</p>
            </div>
        );
    }

    if (!game) {
        return (
            <div className="space-y-4">
                 <div className="p-4"><BackButton text="Back to Groups" /></div>
                 <p className="text-center text-gray-500 py-8">Game not found.</p>
            </div>
        );
    }

    const openCreatePage = () => {
        pageContext?.switchPage(Page.CreatePost, 'Create Post', { groupId: game.id });
    };

    const openInvitePage = () => {
        pageContext?.switchPage(Page.InviteFriends, 'Invite Friends', { groupId: game.id });
    };

    const handleOpenManagePage = () => {
        if (!isCurrentUserAdmin) return;
        pageContext?.switchPage(Page.ManageGroup, 'Manage Group', { groupId: game.id });
    };
    
    const handleReportGroup = (reason: string, details: string) => {
        reportGroup(game, reason, details);
        alert('Group reported. Thank you.');
        setReportModalOpen(false);
    };

    const backgroundEdits = getSafeImageEdits(game.backgroundEdits);
    const backgroundStyle: React.CSSProperties = {
        filter: `brightness(${backgroundEdits.adjustments.brightness}%) contrast(${backgroundEdits.adjustments.contrast}%) saturate(${backgroundEdits.adjustments.saturate}%) blur(${backgroundEdits.adjustments.blur}px)`,
        transform: `scale(${backgroundEdits.adjustments.zoom}) translate(${backgroundEdits.adjustments.offsetX}px, ${backgroundEdits.adjustments.offsetY}px)`,
    };

    const photoEdits = getSafeImageEdits(game.imageEdits);
    const photoStyle: React.CSSProperties = {
        filter: `brightness(${photoEdits.adjustments.brightness}%) contrast(${photoEdits.adjustments.contrast}%) saturate(${photoEdits.adjustments.saturate}%) blur(${photoEdits.adjustments.blur}px)`,
        transform: `scale(${photoEdits.adjustments.zoom}) translate(${photoEdits.adjustments.offsetX}px, ${photoEdits.adjustments.offsetY}px)`,
    };

    const showLeaderboard = game.showLeaderboard !== false;

    return (
        <>
        <div className="space-y-4 -mt-4 -mx-4" onClick={() => setShowOptionsMenu(false)}>
             {/* Header Section */}
            <div className={`relative rounded-b-3xl text-white p-4 overflow-hidden min-h-[350px] flex flex-col justify-start items-center`}>
                 <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: `url(${game.backgroundUrl})`, ...backgroundStyle}}></div>
                 <div className="absolute inset-0 bg-black/50"></div>
                 <div className="absolute top-4 left-4 z-30">
                    <BackButton text="Back to Groups" colorClass="text-white"/>
                 </div>
                 
                 {/* Options Menu (Top Right) */}
                 <div className="absolute top-4 right-4 z-30">
                     <div className="relative" onClick={(e) => e.stopPropagation()}>
                         <button onClick={() => setShowOptionsMenu(!showOptionsMenu)} className="text-white/70 hover:text-white p-2" title="Options">
                             <i className="ph-duotone ph-dots-three-outline-vertical text-2xl"></i>
                         </button>
                         {showOptionsMenu && (
                             <div className="absolute right-0 mt-2 w-48 bg-[var(--primary-dark-purple)] border border-white/20 backdrop-blur-md rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in text-white">
                                 {isCurrentUserAdmin && (
                                     <button 
                                         onClick={() => { setShowOptionsMenu(false); handleOpenManagePage(); }} 
                                         className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors flex items-center gap-2 font-semibold border-b border-white/10"
                                     >
                                         <i className="ph-duotone ph-gear"></i> Manage Group
                                     </button>
                                 )}
                                 <button 
                                     onClick={() => { setShowOptionsMenu(false); setReportModalOpen(true); }} 
                                     className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors flex items-center gap-2 text-red-400 hover:text-red-300 font-semibold"
                                 >
                                     <i className="ph-duotone ph-warning-circle"></i> Report Group
                                 </button>
                             </div>
                         )}
                     </div>
                 </div>

                 <div className="relative z-10 w-full pt-16 flex flex-col items-center">
                    <button
                        ref={profilePicRef}
                        onClick={handleOpenManagePage}
                        disabled={!isCurrentUserAdmin}
                        className={`relative w-24 h-24 rounded-2xl border-4 border-white/50 bg-gray-200 overflow-hidden flex-shrink-0 profile-pic-glow ${isCurrentUserAdmin ? 'cursor-pointer' : ''} disabled:cursor-default`}
                    >
                        <div className={`frame-container ${photoEdits.frame} w-full h-full`}>
                            <img 
                                src={game.imageUrl} 
                                alt={game.name} 
                                className={`w-full h-full object-cover ${photoEdits.filter}`}
                                style={photoStyle}
                            />
                        </div>
                    </button>
                    <div className="text-center mt-3">
                        <h2
                            ref={groupNameRef}
                            onClick={handleOpenManagePage}
                            className={`text-2xl font-bold ${isCurrentUserAdmin ? 'cursor-pointer hover:underline' : ''}`}
                            style={{textShadow: '1px 1px 3px rgba(0,0,0,0.5)'}}
                        >
                            {game.name}
                        </h2>
                        <p className="text-sm mt-1 max-w-sm" style={{textShadow: '1px 1px 3px rgba(0,0,0,0.5)'}}>{game.description}</p>
                    </div>
                </div>

                <div className="absolute bottom-4 z-20 w-full px-4 flex flex-col items-center space-y-4">
                    <div className="flex justify-center items-center space-x-8 w-full max-w-sm mx-auto">
                        <StatItem value={gameMembers.length} label="Members" />
                        <StatItem value={gamePuzzles.length} label="Puzzles" />
                    </div>
                </div>
            </div>
            
            {/* Action Buttons */}
             <div className="px-4">
                 <div className="flex justify-center items-center space-x-4">
                    <button
                        onClick={openInvitePage}
                        className="bg-[var(--primary-dark-purple)] text-white font-bold py-2 px-4 rounded-lg text-sm shadow-md flex items-center gap-2"
                    >
                        <i className="ph-duotone ph-user-plus text-lg"></i> Invite
                    </button>
                    {isCurrentUserAdmin && (
                        <button
                            onClick={handleOpenManagePage}
                            className="bg-[var(--primary-dark-purple)] text-white font-bold py-2 px-4 rounded-lg text-sm shadow-md flex items-center gap-2"
                        >
                           <i className="ph-duotone ph-gear text-lg"></i> Manage
                        </button>
                    )}
                </div>
            </div>
            
            {/* Create a Post Banner */}
            <div className="px-4">
                <div
                    onClick={openCreatePage}
                    className="bg-[var(--primary-dark-purple)] p-3 rounded-lg shadow-md cursor-pointer hover:bg-purple-900 transition-colors"
                >
                    <div className="flex items-center space-x-3">
                        <div className="flex-grow bg-white rounded-lg px-4 py-2 text-center flex items-center justify-center min-h-[40px]">
                            <p
                                className="text-3xl font-bold aurora-text"
                                style={{
                                    fontFamily: "'Caveat', cursive",
                                    transition: 'opacity 0.5s ease-in-out',
                                    opacity: quoteOpacity,
                                }}
                            >
                                {quotes[quoteIndex]}
                            </p>
                        </div>
                        <i className="ph-duotone ph-image text-2xl text-[var(--accent-pink)]"></i>
                    </div>
                </div>
            </div>

            {isCurrentUserAdmin && requestingUsers.length > 0 && (
                <div className="px-4">
                    <div className="p-3 bg-yellow-100 rounded-lg space-y-2">
                        <h3 className="font-bold text-yellow-800">Join Requests ({requestingUsers.length})</h3>
                        {requestingUsers.map(user => (
                            <JoinRequestCard
                                key={user.uid}
                                user={user}
                                onApprove={() => approveJoinRequest(game.id, user.uid)}
                                onDecline={() => declineJoinRequest(game.id, user.uid)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {showLeaderboard && (
                <div className="p-4 sticky top-0 bg-gray-50 z-20">
                    <div className="p-1 rounded-lg flex space-x-1" style={{ backgroundColor: 'var(--primary-dark-purple)'}}>
                        <button onClick={() => setActiveTab('feed')} className={`flex-1 py-2 font-bold rounded-md transition-colors ${activeTab === 'feed' ? 'bg-white text-[var(--primary-dark-purple)]' : 'bg-transparent text-white'}`}>Feed</button>
                        <button onClick={() => setActiveTab('leaderboard')} className={`flex-1 py-2 font-bold rounded-md transition-colors ${activeTab === 'leaderboard' ? 'bg-white text-[var(--primary-dark-purple)]' : 'bg-transparent text-white'}`}>Leaderboard</button>
                    </div>
                </div>
            )}
            
            <div className="px-4 pb-4">
                 {!showLeaderboard || activeTab === 'feed' ? (
                    <>
                        {availableCategories.length > 1 && (
                            <div className="mb-4">
                                <h3 className="text-sm font-bold text-gray-500 mb-2 px-1">Filter by Category</h3>
                                <div className="flex space-x-2 overflow-x-auto pb-2 custom-scrollbar">
                                    {availableCategories.map(category => (
                                        <button
                                            key={category}
                                            onClick={() => setCategoryFilter(category)}
                                            className={`flex-shrink-0 px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                                                categoryFilter === category
                                                    ? 'bg-[var(--primary-dark-purple)] text-white shadow-sm'
                                                    : 'bg-white text-gray-700 hover:bg-gray-200 border'
                                            }`}
                                        >
                                            {category}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div id="game-feed" className="space-y-4">
                            {isLoading ? (
                                <div className="flex justify-center py-12">
                                    <div className="spinner-white" style={{ borderColor: 'rgba(107, 33, 168, 0.3)', borderLeftColor: 'var(--primary-dark-purple)' }}></div>
                                </div>
                            ) : feedWithAds.length > 0 ? (
                                feedWithAds.map(item => {
                                    return <PostCard key={item.id} puzzle={item as Puzzle} onReport={reportPuzzle} />;
                                })
                            ) : (
                                <p className="text-center text-gray-500 py-8">
                                    {categoryFilter === 'All'
                                        ? 'No puzzles in this game yet. Be the first!'
                                        : `No puzzles found in the "${categoryFilter}" category.`}
                                </p>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="space-y-6">
                        {sortedMembers.length >= 3 ? (
                            <>
                                <div className="flex justify-center items-end gap-2 pt-4">
                                    <PodiumCard user={topThree[1]} rank={2} animationDelay="0.2s"/>
                                    <PodiumCard user={topThree[0]} rank={1} animationDelay="0s"/>
                                    <PodiumCard user={topThree[2]} rank={3} animationDelay="0.4s"/>
                                </div>
                                <div className="space-y-2 pt-4">
                                    {rest.map((user, index) => (
                                        <GroupLeaderboardItem key={user.uid} user={user} rank={index + 4} isCurrentUser={user.uid === userProfile?.uid} animationDelay={`${0.2 + index * 0.05}s`} />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="space-y-2 pt-4">
                                {sortedMembers.length > 0 ? sortedMembers.map((user, index) => (
                                    <GroupLeaderboardItem key={user.uid} user={user} rank={index + 1} isCurrentUser={user.uid === userProfile?.uid} animationDelay={`${0.1 + index * 0.05}s`} />
                                )) : (
                                    <p className="text-center text-gray-500 py-8">No members found.</p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
        <ReportPostModal isOpen={isReportModalOpen} onClose={() => setReportModalOpen(false)} onSubmit={handleReportGroup} triggerRect={null} title="Report Group" />
        </>
    );
};

export default GameDetailPage;
